import styled from 'styled-components';
import { classes } from '@ardoq/common-helpers';
import { colors } from '@ardoq/design-tokens';
import type {
  RelationshipsGroupNode,
  RelationshipsLink,
  RelationshipsNode,
} from './types';
import { RelationshipsViewLink } from './RelationshipsViewLink';
import { RelationshipsViewNode } from './RelationshipsViewNode';
import {
  COMPONENT_ID_ATTRIBUTE,
  MAIN_GROUP_CONTAINER_CLASSNAME,
  WORKSPACE_ID_ATTRIBUTE,
} from '@ardoq/global-consts';
import { noop } from 'lodash';
import { ArdoqId, GroupType } from '@ardoq/api-types';
import { BrokenImageMouseEvents } from '@ardoq/icons';
import {
  OnComponentClick,
  OnComponentDoubleClick,
  OnReferenceClick,
} from '../types';

const ViewSvg = styled.svg`
  height: 100%;
  width: 100%;
`;

const GroupPath = styled.path`
  fill: ${colors.grey400};
  stroke: ${colors.grey400};
  stroke-linejoin: round;
  opacity: 0.2;
  filter: url(#darken);
  &.workspace,
  &.fieldGraphNode,
  &.tagGraphNode {
    filter: none;
    opacity: 0.1;
    fill: ${colors.grey400};
    stroke: ${colors.grey400};
  }
`;

type RelationshipsVisualizationProps = BrokenImageMouseEvents & {
  graphNodes: RelationshipsNode[];
  graphGroups: RelationshipsGroupNode[];
  graphLinks: RelationshipsLink[];
  onComponentClick?: OnComponentClick;
  onReferenceClick?: OnReferenceClick;
  onComponentDoubleClick?: OnComponentDoubleClick;
  onCanvasClick?: VoidFunction;
  selectedEntityId?: ArdoqId | null;
  isInitialized: boolean;
};

const RelationshipsVisualization = ({
  graphNodes,
  graphGroups,
  graphLinks,
  onComponentClick = noop,
  onReferenceClick = noop,
  onComponentDoubleClick = noop,
  onCanvasClick = noop,
  selectedEntityId,
  onBrokenImageMouseEnter,
  onBrokenImageMouseLeave,
  isInitialized,
}: RelationshipsVisualizationProps) => (
  <ViewSvg
    style={{ visibility: isInitialized ? 'visible' : 'hidden' }}
    onClick={onCanvasClick}
  >
    <defs>
      <clipPath id="radius-20-clip-path">
        <circle cx="0" cy="0" r="19.5" fill="white" opacity="1" />
      </clipPath>
      <clipPath id="radius-27.5-clip-path">
        <circle cx="0" cy="0" r="26.5" fill="white" opacity="1" />
      </clipPath>
      <filter id="darken">
        <feComponentTransfer>
          <feFuncR type="linear" slope=".5"></feFuncR>
          <feFuncG type="linear" slope=".5"></feFuncG>
          <feFuncB type="linear" slope=".5"></feFuncB>
        </feComponentTransfer>
      </filter>
    </defs>
    <g className={MAIN_GROUP_CONTAINER_CLASSNAME}>
      {graphGroups.map(group => {
        const contextMenuAttributeName = group.isComponent()
          ? COMPONENT_ID_ATTRIBUTE
          : group.type === GroupType.WORKSPACE
            ? WORKSPACE_ID_ATTRIBUTE
            : null;

        const contextMenuDataAttribute = contextMenuAttributeName
          ? { [contextMenuAttributeName]: group.modelId }
          : {};

        return (
          <GroupPath
            key={group.id}
            data-group-id={group.id}
            className={classes(
              'component-group',
              group.getCSS({ useAsBackgroundStyle: false })
            )}
            {...contextMenuDataAttribute}
          />
        );
      })}
      {graphLinks.map(link => (
        <RelationshipsViewLink
          key={link.edge.id}
          link={link}
          onReferenceClick={onReferenceClick}
          selectedReferenceId={selectedEntityId}
        />
      ))}
      {graphNodes.map(graphNode => (
        <RelationshipsViewNode
          key={graphNode.id}
          graphNode={graphNode}
          onComponentClick={onComponentClick}
          onComponentDoubleClick={onComponentDoubleClick}
          selectedComponentId={selectedEntityId}
          onBrokenImageMouseEnter={onBrokenImageMouseEnter}
          onBrokenImageMouseLeave={onBrokenImageMouseLeave}
        />
      ))}
    </g>
  </ViewSvg>
);

export { RelationshipsVisualization };
